/**
 * Published JSON schemas (draft 2020-12) for version 1 of the project, scene,
 * prefab, clips and theme files. `npm run schema` writes them to packages/format/schema so
 * other tools and agents can use them; a test keeps the copies in sync.
 *
 * The schemas check shape only. Rules that need more than one file, such as
 * unique ids, prefab cycles and asset keys, live in validate.ts.
 */

import { CORE_TYPES } from './constants.js';
import { COLOR_MATRIX_LOOKS } from './types.js';
import { GAMEPAD_BUTTONS } from './input.js';

const SCHEMA_BASE = 'https://animo-games.github.io/animo-editor/schema';
const finite = { type: 'number' } as const;

const pluginId = '^[a-z0-9][a-z0-9_-]*(\\.[A-Za-z0-9_-]+)+$';
const projectPath = { type: 'string', minLength: 1, not: { pattern: '^(/|[A-Za-z]:|.*\\\\)' } } as const;
const time0 = { type: 'number', minimum: 0 } as const;

const rgb = { type: 'integer', minimum: 0, maximum: 0xffffff } as const;
const range = {
  anyOf: [finite, { type: 'object', required: ['min', 'max'], properties: { min: finite, max: finite }, additionalProperties: false }],
} as const;
const tween = {
  anyOf: [finite, { type: 'object', required: ['start', 'end'], properties: { start: finite, end: finite }, additionalProperties: false }],
} as const;

const filterProps = (type: string, props: Record<string, unknown>) => ({
  type: 'object',
  required: ['type'],
  properties: { type: { const: type }, enabled: { type: 'boolean' }, ...props },
  additionalProperties: false,
});

const filterDef = {
  anyOf: [
    filterProps('glow', { color: rgb, outerStrength: finite, innerStrength: finite, knockout: { type: 'boolean' } }),
    filterProps('shadow', { x: finite, y: finite, decay: finite, power: finite, color: rgb, intensity: finite }),
    filterProps('blur', { quality: { enum: [0, 1, 2] }, x: finite, y: finite, strength: finite, steps: { type: 'integer', minimum: 1 } }),
    filterProps('colorMatrix', { look: { enum: [...COLOR_MATRIX_LOOKS] }, amount: finite }),
    filterProps('vignette', { x: finite, y: finite, radius: finite, strength: finite, color: rgb }),
    filterProps('pixelate', { amount: finite }),
    filterProps('mask', { object: { type: 'string', minLength: 1 }, texture: { type: 'string', minLength: 1 }, invert: { type: 'boolean' } }),
    filterProps('ping', { color: rgb, strength: finite, width: finite, softness: finite, speed: finite, every: finite, delay: finite }),
    filterProps('crt', { lines: finite, scroll: finite, amount: finite, shift: finite }),
    filterProps('wipe', { width: finite, direction: { enum: ['left', 'right', 'up', 'down'] }, reveal: { type: 'boolean' }, progress: finite }),
  ],
} as const;

const zoneDef = {
  anyOf: [
    { type: 'object', required: ['shape'], properties: { shape: { const: 'point' } }, additionalProperties: false },
    {
      type: 'object',
      required: ['shape', 'width', 'height'],
      properties: { shape: { const: 'box' }, x: finite, y: finite, width: { type: 'number', minimum: 0 }, height: { type: 'number', minimum: 0 }, edge: { type: 'boolean' } },
      additionalProperties: false,
    },
    {
      type: 'object',
      required: ['shape', 'radius'],
      properties: { shape: { const: 'circle' }, x: finite, y: finite, radius: { type: 'number', minimum: 0 }, edge: { type: 'boolean' } },
      additionalProperties: false,
    },
    {
      type: 'object',
      required: ['shape', 'x1', 'y1', 'x2', 'y2'],
      properties: { shape: { const: 'line' }, x1: finite, y1: finite, x2: finite, y2: finite },
      additionalProperties: false,
    },
  ],
} as const;

const allProperties = {
  id: { type: 'string', minLength: 1, pattern: '^[^/]+$' },
  type: { type: 'string', minLength: 1 },
  name: { type: 'string' },
  x: finite,
  y: finite,
  scaleX: finite,
  scaleY: finite,
  angle: finite,
  depth: finite,
  alpha: { type: 'number', minimum: 0, maximum: 1 },
  visible: { type: 'boolean' },
  scrollFactorX: finite,
  scrollFactorY: finite,
  blendMode: { type: ['number', 'string'] },
  lit: { type: 'boolean' },
  filters: { type: 'array', items: { $ref: '#/$defs/filter' } },
  originX: finite,
  originY: finite,
  flipX: { type: 'boolean' },
  flipY: { type: 'boolean' },
  tint: { type: 'integer', minimum: 0, maximum: 0xffffff },
  texture: {
    type: 'object',
    required: ['key'],
    properties: { key: { type: 'string', minLength: 1 }, frame: { type: ['string', 'integer'] } },
    additionalProperties: false,
  },
  animation: {
    type: 'object',
    required: ['key'],
    properties: { key: { type: 'string', minLength: 1 }, autoplay: { type: 'boolean' } },
    additionalProperties: false,
  },
  text: { type: 'string' },
  colors: { type: 'object', additionalProperties: { type: 'string', minLength: 1 } },
  themeStyle: { type: 'string', minLength: 1 },
  style: { type: 'object' },
  font: { type: 'string', minLength: 1 },
  fontSize: { type: 'number', minimum: 0 },
  align: { type: 'string' },
  letterSpacing: finite,
  lineSpacing: finite,
  maxWidth: { type: 'number', minimum: 0 },
  leftWidth: { type: 'number', minimum: 0 },
  rightWidth: { type: 'number', minimum: 0 },
  topHeight: { type: 'number', minimum: 0 },
  bottomHeight: { type: 'number', minimum: 0 },
  width: { type: 'number', minimum: 0 },
  height: { type: 'number', minimum: 0 },
  tilePositionX: finite,
  tilePositionY: finite,
  tileScaleX: finite,
  tileScaleY: finite,
  scrollSpeedX: finite,
  scrollSpeedY: finite,
  frames: { type: 'array', items: { type: ['string', 'integer'] } },
  burst: { type: 'boolean' },
  emitting: { type: 'boolean' },
  frequency: finite,
  quantity: { type: 'integer', minimum: 0 },
  maxAlive: { type: 'integer', minimum: 0 },
  stopAfter: { type: 'number', minimum: 0 },
  lifespan: range,
  speed: range,
  direction: range,
  rotate: range,
  gravityX: finite,
  gravityY: finite,
  scale: tween,
  particleAlpha: tween,
  color: {
    anyOf: [rgb, { type: 'object', required: ['start', 'end'], properties: { start: rgb, end: rgb }, additionalProperties: false }],
  },
  ease: { type: 'string', minLength: 1 },
  zone: { $ref: '#/$defs/zone' },
  intensity: { type: 'number', minimum: 0 },
  radius: { type: 'number', minimum: 0 },
  z: finite,
  key: { type: 'string', minLength: 1 },
  autoplay: { type: 'boolean' },
  loop: { type: 'boolean' },
  muted: { type: 'boolean' },
  playbackRate: { type: 'number', exclusiveMinimum: 0 },
  children: { type: 'array', items: { $ref: '#/$defs/object' } },
  map: { type: 'string', minLength: 1 },
  layer: { type: 'string', minLength: 1 },
  prefab: projectPath,
  overrides: {
    type: 'object',
    propertyNames: { pattern: '^[^/]+(/[^/]+)*$' },
    additionalProperties: { type: 'object' },
  },
  clips: {
    type: 'object',
    required: ['file'],
    properties: { file: projectPath, autoplay: { type: 'string', minLength: 1 } },
    additionalProperties: false,
  },
  connections: {
    type: 'array',
    items: {
      type: 'object',
      required: ['on', 'do'],
      properties: {
        on: { type: 'string', minLength: 1 },
        who: { type: 'array', items: { type: 'string' } },
        name: { type: 'string' },
        to: { type: 'string' },
        do: { type: 'string', minLength: 1 },
        args: { type: 'array' },
      },
      additionalProperties: false,
    },
  },
  scripts: {
    type: 'object',
    propertyNames: projectPath,
    additionalProperties: { type: ['object', 'null'] },
  },
  data: { $ref: '#/$defs/pluginData' },
} as const;

/** Fields every object has, whatever its type: its place in the scene and how it draws. */
const COMMON = [
  'id', 'type', 'name', 'x', 'y', 'scaleX', 'scaleY', 'angle', 'depth', 'alpha', 'visible', 'scrollFactorX', 'scrollFactorY',
  'blendMode', 'lit', 'filters', 'children', 'overrides', 'clips', 'connections', 'scripts', 'data',
] as const;
const commonProperties = Object.fromEntries(COMMON.map((k) => [k, allProperties[k]])) as Pick<typeof allProperties, (typeof COMMON)[number]>;
const coreProperties = Object.fromEntries(Object.entries(allProperties).filter(([k]) => !(COMMON as readonly string[]).includes(k))) as Omit<typeof allProperties, (typeof COMMON)[number]>;

const objectDef = {
  type: 'object',
  required: ['id', 'type'],
  properties: commonProperties,
  allOf: [
    // Core types' own fields. A plugin type's top-level fields are its own, so the same name can mean
    // something else there, such as a button's texture key or a bar's ease time.
    { if: { properties: { type: { enum: [...CORE_TYPES] } } }, then: { properties: coreProperties } },
    { if: { properties: { type: { const: 'tilemapLayer' } } }, then: { required: ['map', 'layer'] } },
    { if: { properties: { type: { const: 'prefab' } } }, then: { required: ['prefab'], not: { required: ['children'] } } },
    {
      if: { properties: { type: { not: { enum: [...CORE_TYPES] } } } },
      then: { properties: { type: { pattern: pluginId } } },
    },
  ],
} as const;

const defs = {
  object: objectDef,
  filter: filterDef,
  zone: zoneDef,
  pluginData: { type: 'object', propertyNames: { pattern: pluginId } },
} as const;

const tilemapRefs = {
  type: 'array',
  items: {
    type: 'object',
    required: ['id', 'file'],
    properties: { id: { type: 'string', minLength: 1 }, file: projectPath },
    additionalProperties: false,
  },
} as const;

export const sceneSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: `${SCHEMA_BASE}/scene.v1.schema.json`,
  title: 'animo-editor scene',
  type: 'object',
  required: ['formatVersion', 'key'],
  properties: {
    formatVersion: { const: 1 },
    key: { type: 'string', minLength: 1 },
    preload: { type: 'array', items: projectPath },
    animations: { type: 'array', items: projectPath },
    tilemaps: tilemapRefs,
    sound: {
      type: 'object',
      properties: { music: { type: ['string', 'null'] }, ambience: { type: ['string', 'null'] }, fade: time0 },
      additionalProperties: false,
    },
    camera: {
      type: 'object',
      properties: {
        backgroundColor: { type: 'string' },
        zoom: { type: 'number', exclusiveMinimum: 0 },
        follow: { type: 'string', minLength: 1 },
        lerp: { type: 'number', minimum: 0, maximum: 1 },
        bounds: {
          anyOf: [
            { const: 'tilemaps' },
            {
              type: 'object',
              required: ['x', 'y', 'width', 'height'],
              properties: { x: finite, y: finite, width: { type: 'number', minimum: 0 }, height: { type: 'number', minimum: 0 } },
              additionalProperties: false,
            },
          ],
        },
        roundPixels: { type: 'boolean' },
        filters: { type: 'array', items: { $ref: '#/$defs/filter' } },
      },
      additionalProperties: false,
    },
    lights: {
      type: 'object',
      properties: { enabled: { type: 'boolean' }, ambient: rgb },
      additionalProperties: false,
    },
    children: { type: 'array', items: { $ref: '#/$defs/object' } },
    data: { $ref: '#/$defs/pluginData' },
  },
  additionalProperties: false,
  $defs: defs,
} as const;

export const prefabSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: `${SCHEMA_BASE}/prefab.v1.schema.json`,
  title: 'animo-editor prefab',
  type: 'object',
  required: ['formatVersion', 'root'],
  properties: {
    formatVersion: { const: 1 },
    tilemaps: tilemapRefs,
    root: { $ref: '#/$defs/object' },
  },
  additionalProperties: false,
  $defs: defs,
} as const;

export const projectSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: `${SCHEMA_BASE}/project.v1.schema.json`,
  title: 'animo-editor project',
  type: 'object',
  required: ['formatVersion'],
  properties: {
    formatVersion: { const: 1 },
    name: { type: 'string' },
    root: { type: 'string' },
    resolution: {
      type: 'object',
      required: ['width', 'height'],
      properties: { width: { type: 'integer', minimum: 1 }, height: { type: 'integer', minimum: 1 } },
      additionalProperties: false,
    },
    scenes: { type: 'array', items: projectPath },
    plugins: { type: 'array', items: { type: 'string', minLength: 1 } },
    settings: { $ref: '#/$defs/pluginData' },
    theme: projectPath,
    sounds: projectPath,
    couch: {
      type: 'object',
      properties: { slug: { type: 'string', pattern: '^[A-Za-z0-9][A-Za-z0-9_-]*$' } },
    },
    input: {
      type: 'object',
      required: ['actions'],
      properties: {
        players: { type: 'integer', minimum: 1 },
        actions: {
          type: 'array',
          items: {
            type: 'object',
            required: ['name'],
            properties: {
              name: { type: 'string', minLength: 1 },
              description: { type: 'string' },
              pad: { type: 'array', items: { enum: [...GAMEPAD_BUTTONS] } },
              players: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    keys: { type: 'array', items: { type: 'string', minLength: 1 } },
                  },
                  additionalProperties: false,
                },
              },
            },
            additionalProperties: false,
          },
        },
      },
      additionalProperties: false,
    },
    editor: {
      type: 'object',
      properties: {
        grid: {
          type: 'object',
          required: ['width', 'height'],
          properties: { width: { type: 'number', exclusiveMinimum: 0 }, height: { type: 'number', exclusiveMinimum: 0 } },
        },
        snap: { type: 'boolean' },
      },
    },
  },
  additionalProperties: false,
  $defs: { pluginData: defs.pluginData },
} as const;

const time = { type: 'number', minimum: 0 } as const;
const target = { type: 'string' } as const;

export const clipsSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: `${SCHEMA_BASE}/clips.v1.schema.json`,
  title: 'animo-editor clips',
  type: 'object',
  required: ['formatVersion', 'clips'],
  properties: {
    formatVersion: { const: 1 },
    clips: { type: 'object', propertyNames: { minLength: 1 }, additionalProperties: { $ref: '#/$defs/clip' } },
  },
  additionalProperties: false,
  $defs: {
    clip: {
      type: 'object',
      required: ['duration'],
      properties: {
        duration: time,
        repeat: { type: 'integer', minimum: -1 },
        yoyo: { type: 'boolean' },
        repeatDelay: time,
        tracks: { type: 'array', items: { $ref: '#/$defs/track' } },
      },
      additionalProperties: false,
    },
    track: {
      anyOf: [
        {
          type: 'object',
          required: ['field', 'keys'],
          properties: {
            target,
            field: { type: 'string', minLength: 1 },
            script: projectPath,
            relative: { type: 'boolean' },
            keys: {
              type: 'array',
              items: {
                type: 'object',
                required: ['t', 'v'],
                properties: { t: time, v: {}, ease: { type: 'string', minLength: 1 } },
                additionalProperties: false,
              },
            },
          },
          additionalProperties: false,
        },
        {
          type: 'object',
          required: ['play'],
          properties: {
            target,
            play: {
              type: 'array',
              items: {
                type: 'object',
                required: ['t', 'anim'],
                properties: { t: time, anim: { type: 'string' } },
                additionalProperties: false,
              },
            },
          },
          additionalProperties: false,
        },
        {
          type: 'object',
          required: ['events'],
          properties: {
            target,
            events: {
              type: 'array',
              items: {
                type: 'object',
                required: ['t'],
                properties: { t: time, call: { type: 'string', minLength: 1 }, event: { type: 'string', minLength: 1 }, args: { type: 'array' } },
                additionalProperties: false,
              },
            },
          },
          additionalProperties: false,
        },
        {
          type: 'object',
          required: ['sounds'],
          properties: {
            sounds: {
              type: 'array',
              items: {
                type: 'object',
                required: ['t', 'sound'],
                properties: { t: time, sound: { type: 'string', minLength: 1 } },
                additionalProperties: false,
              },
            },
          },
          additionalProperties: false,
        },
      ],
    },
  },
} as const;

const color = { type: 'string', minLength: 1 } as const;
const styleName = { type: 'string', minLength: 1 } as const;
const padding = {
  type: 'object',
  properties: { x: { type: 'number', minimum: 0 }, y: { type: 'number', minimum: 0 } },
  additionalProperties: false,
} as const;
const named = (def: object) => ({ type: 'object', propertyNames: { minLength: 1 }, additionalProperties: def }) as const;

const panelProps = {
  fill: color,
  fillAlpha: { type: 'number', minimum: 0, maximum: 1 },
  stroke: color,
  strokeWidth: { type: 'number', minimum: 0 },
  radius: { type: 'number', minimum: 0 },
  texture: {
    type: 'object',
    required: ['key'],
    properties: { key: { type: 'string', minLength: 1 }, frame: { type: ['string', 'integer'] } },
    additionalProperties: false,
  },
  slice: {
    type: 'object',
    required: ['left', 'right', 'top', 'bottom'],
    properties: { left: { type: 'number', minimum: 0 }, right: { type: 'number', minimum: 0 }, top: { type: 'number', minimum: 0 }, bottom: { type: 'number', minimum: 0 } },
    additionalProperties: false,
  },
  padding,
} as const;

const buttonStateProps = {
  panel: styleName,
  label: styleName,
  labelColor: color,
  scale: { type: 'number', exclusiveMinimum: 0 },
  padding,
  sound: { type: 'string' },
  alpha: { type: 'number', minimum: 0, maximum: 1 },
} as const;

export const themeSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: `${SCHEMA_BASE}/theme.v1.schema.json`,
  title: 'animo-editor theme',
  type: 'object',
  required: ['formatVersion'],
  properties: {
    formatVersion: { const: 1 },
    rendering: { enum: ['smooth', 'pixel'] },
    colors: named({ type: 'string', pattern: '^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$' }),
    fonts: named({
      type: 'object',
      properties: {
        url: projectPath,
        weight: { type: 'string' },
        style: { type: 'string' },
        bitmap: projectPath,
        texture: projectPath,
        size: { type: 'number', exclusiveMinimum: 0 },
      },
      additionalProperties: false,
    }),
    text: named({ type: 'object' }),
    buttons: named({
      type: 'object',
      properties: {
        ...buttonStateProps,
        hover: { type: 'object', properties: buttonStateProps, additionalProperties: false },
        pressed: { type: 'object', properties: buttonStateProps, additionalProperties: false },
        disabled: { type: 'object', properties: buttonStateProps, additionalProperties: false },
      },
      additionalProperties: false,
    }),
    panels: named({
      type: 'object',
      properties: { ...panelProps, selected: { type: 'object', properties: panelProps, additionalProperties: false } },
      additionalProperties: false,
    }),
    bars: named({
      type: 'object',
      properties: {
        panel: styleName,
        fill: color,
        fillPanel: styleName,
        inset: { type: 'number', minimum: 0 },
        direction: { enum: ['right', 'left', 'up', 'down'] },
      },
      additionalProperties: false,
    }),
    defaults: {
      type: 'object',
      properties: { text: styleName, button: styleName, panel: styleName, bar: styleName },
      additionalProperties: false,
    },
  },
  additionalProperties: false,
} as const;

const unit = { type: 'number', minimum: 0, maximum: 1 } as const;
const busFilter = {
  type: 'object',
  required: ['type'],
  properties: {
    type: { enum: ['lowpass', 'highpass', 'reverb', 'echo', 'compressor', 'distortion'] },
    enabled: { type: 'boolean' },
    cutoff: { type: 'number', minimum: 10, maximum: 22050 },
    resonance: { type: 'number', minimum: 0.1, maximum: 30 },
    room: { enum: ['small', 'medium', 'large'] },
    mix: unit,
    delay: { type: 'number', minimum: 0.01, maximum: 2 },
    feedback: { type: 'number', minimum: 0, maximum: 0.95 },
    threshold: { type: 'number', minimum: -100, maximum: 0 },
    ratio: { type: 'number', minimum: 1, maximum: 20 },
    amount: unit,
  },
  additionalProperties: false,
} as const;
const bus = {
  type: 'object',
  properties: { volume: unit, filters: { type: 'array', items: busFilter } },
  additionalProperties: false,
} as const;

export const soundsSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: `${SCHEMA_BASE}/sounds.v1.schema.json`,
  title: 'animo-editor sounds',
  type: 'object',
  required: ['formatVersion'],
  properties: {
    formatVersion: { const: 1 },
    preload: { type: 'array', items: projectPath },
    loadLater: { type: 'array', items: projectPath },
    volume: { type: 'number', minimum: 0, maximum: 1 },
    sounds: named({
      type: 'object',
      required: ['keys'],
      properties: {
        keys: { type: 'array', items: { type: 'string', minLength: 1 } },
        bus: { enum: ['effects', 'music', 'ambience'] },
        volume: { type: 'number', minimum: -60, maximum: 12 },
        pitch: { type: 'number', minimum: 0, maximum: 1 },
        rate: { type: 'number', minimum: 0.1, maximum: 4 },
        pick: { enum: ['random', 'order'] },
        max: { type: 'integer', minimum: 0 },
        loop: { type: 'boolean' },
      },
      additionalProperties: false,
    }),
    buses: {
      type: 'object',
      properties: { effects: bus, music: bus, ambience: bus },
      additionalProperties: false,
    },
  },
  additionalProperties: false,
} as const;

export const schemas = {
  'project.v1.schema.json': projectSchema,
  'scene.v1.schema.json': sceneSchema,
  'prefab.v1.schema.json': prefabSchema,
  'clips.v1.schema.json': clipsSchema,
  'theme.v1.schema.json': themeSchema,
  'sounds.v1.schema.json': soundsSchema,
} as const;
